import { FlowArrow } from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import { Switch } from '~/components/ui/switch';
import { Tip } from '~/components/ui/tip';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useSetWorkflowAutoApproval } from '~/hooks/mutations/use-workflow-auto-approval';
import type { WorkflowSchedule } from '~/hooks/queries/use-workflow-schedules';

interface WorkflowScheduleListProps {
  schedules: WorkflowSchedule[];
}

/** The cron expression as stored, since the workflow service keeps no friendlier form. */
export function formatWorkflowSchedule(schedule: WorkflowSchedule): string {
  const parts = [schedule.expression ?? 'Cron schedule'];
  if (schedule.timezone) parts.push(`(${schedule.timezone})`);
  return parts.join(' ');
}

export const AUTO_APPROVAL_LABEL = 'Runs without asking';

/**
 * FRONT-2215: the page lists colleagues' shared workflows too, and the only
 * other thing setting them apart is a switch that is absent — which reads as a
 * missing control rather than someone else's row. Visible text, not a hover
 * hint: on a touch screen there is no hover.
 */
export const SHARED_LABEL = 'Shared with you';

/** What the switch means, in the words the owner reads before flipping it. */
export function autoApprovalHint(approved: boolean): string {
  return approved
    ? 'Actions that would normally ask for your OK run on their own in this workflow. Turn off to have them refused again while nobody is watching.'
    : 'Scheduled runs have nobody to answer an approval card, so an action that needs your OK is refused. Turn on to let this workflow run those actions without asking.';
}

/**
 * The owner's per-workflow approval for unattended tier-3 actions (FRONT-1811).
 * Lives on the schedule row because a scheduled run is the one that has nobody
 * to click. Only the owner sees it: the approval is theirs, and a colleague's
 * row would show a switch they cannot flip.
 */
function AutoApprovalSwitch({ schedule }: { schedule: WorkflowSchedule }) {
  const mutation = useSetWorkflowAutoApproval();
  const approved = schedule.autoApproved;
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <span
      className="flex items-center gap-2 shrink-0"
      onClick={stop}
      onKeyDown={stop}
      data-testid="workflow-auto-approval"
    >
      <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
        {AUTO_APPROVAL_LABEL}
      </span>
      <Tip label={autoApprovalHint(approved)}>
        <Switch
          checked={approved}
          disabled={mutation.isPending}
          aria-label={`${AUTO_APPROVAL_LABEL}: ${schedule.workflowTitle}`}
          onCheckedChange={(next) => mutation.mutate({ workflowId: schedule.workflowId, approved: next })}
        />
      </Tip>
    </span>
  );
}

/**
 * Scheduled workflows on the Schedule page. A workflow's trigger is edited in
 * the workflow editor, so a row is a link there rather than a second place to
 * change it; the only control on the row is the owner's unattended approval.
 */
export function WorkflowScheduleList({ schedules }: WorkflowScheduleListProps) {
  const navigate = useNavigate();

  // No skeleton: this section is additive, and a placeholder on every load
  // would promise a block that most users do not have.
  if (!schedules.length) return null;

  return (
    <div className="mt-3 rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
      {schedules.map((schedule) => (
        <div
          key={schedule.triggerId}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/workflows/${schedule.workflowId}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/workflows/${schedule.workflowId}`);
            }
          }}
          className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer focus-visible:outline-none overflow-hidden"
        >
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${schedule.enabled ? 'bg-success' : 'bg-muted-foreground/40'}`}
          />
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm font-medium text-foreground truncate">{schedule.workflowTitle}</span>
                </TooltipTrigger>
                <TooltipContent>{schedule.workflowTitle}</TooltipContent>
              </Tooltip>
              <span className="flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
                <FlowArrow size={10} weight="fill" /> Workflow
              </span>
              {!schedule.ownedByCaller && (
                <span className="shrink-0 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/80">
                  {SHARED_LABEL}
                </span>
              )}
              {!schedule.enabled && (
                <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/80">
                  Paused
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[length:var(--text-2xs)] text-muted-foreground truncate">
              {formatWorkflowSchedule(schedule)}
            </div>
          </div>
          {schedule.ownedByCaller && <AutoApprovalSwitch schedule={schedule} />}
        </div>
      ))}
    </div>
  );
}
